import type { Logger } from "@expert/logging";

import { removePii } from "@asurion-core/expert-workspace-pii";
import { sendMetrics } from "@expert/cloudwatch-metrics";

import type {
    ShadowComparison,
    ShadowComparisonResult,
    ShadowComparisonStatus,
    ShadowDiffEntry,
    ShadowTallySummary,
} from "./types";

import { TALLY_COUNT_KEY } from "./ShadowTally";

/**
 * Share of sessions whose shadow comparisons are logged. The analytic (`onComparison`) and the tally
 * summary are not sampled, so EDP keeps the complete record; logs only need enough sessions to read
 * real before/after values. At 100% this was about half of all prod frontend log lines.
 */
export const LOG_SAMPLE_RATE = 0.1;

/** PII-safe projection of a comparison — paths only, never raw values. Safe to emit as an analytic. */
export function toShadowComparisonResult(comparison: ShadowComparison): ShadowComparisonResult {
    const diffPaths = comparison.diffs.map((diff) => diff.path);
    return {
        status: comparison.status,
        eventName: comparison.eventName,
        correlationId: comparison.correlationId,
        sessionId: comparison.sessionId,
        diffCount: diffPaths.length,
        diffPaths,
        latencyDeltaMs: comparison.latencyDeltaMs,
        ...(comparison.gaiaCues ? { gaiaCues: comparison.gaiaCues } : {}),
        ...(comparison.simplrCues ? { simplrCues: comparison.simplrCues } : {}),
    };
}

/**
 * Events whose bodies carry raw customer context (e.g. `update-session-context-success` echoes the
 * MDN, account number and free-text session remarks the frontend set). `removePii` cannot be relied
 * on for those, so their diffs are logged as paths only, never values. Today these pairs are presence-only
 * (`isPresenceOnly`) and never diffed; this stays as a backstop in case that changes.
 */
const PATHS_ONLY_LOG_EVENTS: ReadonlySet<string> = new Set(["update-session-context-success"]);

/** CloudWatch metric name for a comparison status. */
export function metricNameForStatus(status: ShadowComparisonStatus): string {
    switch (status) {
        case "equal":
            return "gaiaSimplr.match";
        case "different":
            return "gaiaSimplr.mismatch";
        case "gaia-only":
            return "gaiaSimplr.gaiaOnly";
        case "simplr-only":
            return "gaiaSimplr.simplrOnly";
        default:
            return "gaiaSimplr.unknown";
    }
}

/**
 * True when this session's shadow comparisons should be logged. Hashes the session id so every
 * comparison of a sampled session is kept (a readable story per session) and the choice is stable
 * across reloads; comparisons without a session id fall back to a coin flip.
 */
export function isSessionSampled(sessionId: string | undefined, rate: number = LOG_SAMPLE_RATE): boolean {
    if (rate >= 1) return true;
    if (rate <= 0) return false;
    if (!sessionId) return Math.random() < rate;
    return hashToUnitInterval(sessionId) < rate;
}

/** 32-bit FNV-1a with a murmur3 finalizer (so near-identical ids still spread), scaled to [0, 1). */
function hashToUnitInterval(value: string): number {
    let hash = 0x811c9dc5;
    for (let index = 0; index < value.length; index++) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x85ebca6b);
    hash ^= hash >>> 13;
    hash = Math.imul(hash, 0xc2b2ae35);
    hash ^= hash >>> 16;
    return (hash >>> 0) / 0x1_0000_0000;
}

/**
 * Emits a GAIA-vs-Simplr comparison (matching the feature-flag dark launch in
 * `@asurion-core/expert-workspace-feature-flagging`):
 *  - **analytic** — the PII-safe result (paths only) via `onComparison`, for every non-equal comparison,
 *  - **log** — for {@link LOG_SAMPLE_RATE} of sessions only: matches at `debug`, `*-only` at `info`, and
 *    the differing fields with before/after values scrubbed of PII.
 *
 * Counts (equal included) and CloudWatch metrics come from the tally summary, see {@link reportShadowSummary}.
 */
export function reportShadowComparison(
    comparison: ShadowComparison,
    logger: Logger,
    onComparison?: (result: ShadowComparisonResult) => void,
): void {
    const result = toShadowComparisonResult(comparison);

    // Surface the PII-safe analytic (paths only).
    if (comparison.status !== "equal") onComparison?.(result);

    if (!isSessionSampled(comparison.sessionId)) return;

    if (comparison.status === "equal") {
        logger.debug(toShadowLogFields(result), "GAIA/Simplr shadow | messages match");
        return;
    }

    if (comparison.status === "different" && comparison.diffs.length > 0) {
        if (PATHS_ONLY_LOG_EVENTS.has(comparison.eventName)) {
            logger.warn(toShadowLogFields(result), "GAIA/Simplr shadow | fields differ");
            return;
        }
        // Log the differing fields with values scrubbed of PII so they are searchable in OpenSearch.
        void logRedactedDiffs(comparison, logger);
    } else {
        logger.info(toShadowLogFields(result), `GAIA/Simplr shadow | ${comparison.status}`);
    }
}

/**
 * One CloudWatch datapoint per event x status seen in the window, all sent in a single request. Dimensions
 * stay low-cardinality: the status metric name and the GAIA event name, never values or session ids.
 */
export function toShadowSummaryMetrics(summary: ShadowTallySummary): Parameters<typeof sendMetrics>[0] {
    const metrics: Parameters<typeof sendMetrics>[0] = [];
    for (const [shadowEvent, counts] of Object.entries(summary.counts)) {
        for (const [status, countKey] of Object.entries(TALLY_COUNT_KEY)) {
            const value = counts[countKey];
            if (value > 0) {
                metrics.push({
                    metrics: [{ name: "Event", value }],
                    dimensions: { eventName: metricNameForStatus(status as ShadowComparisonStatus), shadowEvent },
                });
            }
        }
    }
    return metrics;
}

/** Sends the window's metrics in one request, then hands the summary to the app's analytic. */
export function reportShadowSummary(
    summary: ShadowTallySummary,
    onSummary?: (summary: ShadowTallySummary) => void,
): void {
    const metrics = toShadowSummaryMetrics(summary);
    if (metrics.length > 0) void sendMetrics(metrics);
    onSummary?.(summary);
}

/**
 * Log payload for a {@link ShadowComparisonResult}. The status goes under `shadowStatus` because `status` is a
 * reserved log field; `onComparison` still receives `status`.
 */
function toShadowLogFields(
    result: ShadowComparisonResult,
): Omit<ShadowComparisonResult, "status"> & { shadowStatus: ShadowComparisonStatus } {
    const { status, ...rest } = result;
    return { ...rest, shadowStatus: status };
}

async function logRedactedDiffs(comparison: ShadowComparison, logger: Logger): Promise<void> {
    try {
        const diffs = await Promise.all(
            comparison.diffs.map(async (diff) => ({
                path: diff.path,
                gaia: await redactValue(diff.gaiaValue),
                simplr: await redactValue(diff.simplrValue),
            })),
        );
        logger.warn(
            {
                eventName: comparison.eventName,
                sessionId: comparison.sessionId,
                correlationId: comparison.correlationId,
                shadowStatus: comparison.status,
                latencyDeltaMs: comparison.latencyDeltaMs,
                diffs,
            },
            "GAIA/Simplr shadow | fields differ",
        );
    } catch (error: unknown) {
        logger.warn({ error, eventName: comparison.eventName }, "GAIA/Simplr shadow | failed to log field diffs");
    }
}

async function redactValue(value: ShadowDiffEntry["gaiaValue"]): Promise<string | undefined> {
    if (value === undefined) return undefined;
    const text = typeof value === "string" ? value : JSON.stringify(value);
    if (text === undefined) return undefined;
    return removePii(text);
}
